import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Paper,
} from '@mui/material';
import { Restaurant, LocalDining, AccessTime } from '@mui/icons-material';
import { client } from '../api/Api';

function Recipes() {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadRecipes();
  }, []);

  const loadRecipes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await client.get('/recipes/suggestions', {
        params: { limit: 20 }
      });
      setRecipes(response.data || []);
    } catch (e) {
      console.error('Error loading recipes:', e);
      setError('Nepavyko užkrauti receptų. Bandykite vėliau.');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (price == null) return null;
    return `€${price.toFixed(2)}`;
  };

  const handleRecipeClick = (recipe) => {
    if (recipe.id) {
      navigate(`/recipe/${recipe.id}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 4 }}>
      <Container maxWidth="lg" sx={{ pt: 4, pb: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Patiekalų ir Užkandžių Pasiūlymai
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            AI sugeneruoti receptai su ingredientais ir kainomis
          </Typography>
        </Box>

        {recipes.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Receptų nerasta. Patikrinkite vėliau.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {recipes.map((recipe) => (
              <Grid item xs={12} sm={6} md={4} key={recipe.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6,
                    },
                  }}
                  onClick={() => handleRecipeClick(recipe)}
                >
                  {/* Image */}
                  {recipe.imageUrl ? (
                    <CardMedia
                      component="img"
                      height="200"
                      image={recipe.imageUrl}
                      alt={recipe.titleLt || recipe.title || 'Receptas'}
                      sx={{ objectFit: 'cover' }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 200,
                        bgcolor: 'grey.200',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <LocalDining sx={{ fontSize: 60, color: 'grey.400' }} />
                    </Box>
                  )}

                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Title */}
                    <Typography
                      variant="h6"
                      component="h2"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        minHeight: '3em',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {recipe.titleLt || recipe.title || 'Receptas'}
                    </Typography>

                    {/* Chips */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      {recipe.caloriesKcal != null && (
                        <Chip
                          label={`${Math.round(recipe.caloriesKcal)} kcal`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                      {recipe.type && (
                        <Chip
                          label={recipe.type === 'food' ? 'Patiekalas' : 'Gėrimas'}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    {/* Price info */}
                    {(recipe.totalUsedCost != null || recipe.totalPurchaseCost != null) && (
                      <Box sx={{ mb: 2 }}>
                        {recipe.totalUsedCost != null && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Sunaudota:</strong> {formatPrice(recipe.totalUsedCost)}
                          </Typography>
                        )}
                        {recipe.totalPurchaseCost != null && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Nupirkta:</strong> {formatPrice(recipe.totalPurchaseCost)}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Ingredients preview */}
                    {recipe.ingredients && recipe.ingredients.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                          Ingredientai ({recipe.ingredients.length}):
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {recipe.ingredients.slice(0, 3).map(ing => ing.name).join(', ')}
                          {recipe.ingredients.length > 3 && '...'}
                        </Typography>
                      </Box>
                    )}

                    {/* Instructions preview */}
                    {recipe.instructionsLt && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          flexGrow: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          mb: 2,
                        }}
                      >
                        {recipe.instructionsLt}
                      </Typography>
                    )}

                    {/* View button */}
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        mt: 'auto',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                        },
                      }}
                    >
                      Peržiūrėti receptą
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
}

export default Recipes;

